import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { CourseDraft } from '@/pages/courses/api';

interface ModeCard {
  value: string;
  title: string;
  body: string;
  // Rendered when the option can't be picked in the current combination.
  blockedNote?: string;
}

const ACCESS_MODES: ModeCard[] = [
  {
    value: 'login',
    title: 'Log-in required',
    body: 'The link asks for a quick sign-in before progress is recorded. Required for assignment and per-person tracking.',
  },
  {
    value: 'open',
    title: 'Open link',
    body: 'Anyone holding the link can complete the course — no account needed. Progress is counted anonymously.',
  },
];

const TRACKING_MODES: ModeCard[] = [
  {
    value: 'individual',
    title: 'Individually tracked',
    body: 'See who specifically completed the course and when. Feeds each learner’s record.',
    blockedNote: 'Needs log-in',
  },
  {
    value: 'count_only',
    title: 'Completed count only',
    body: 'Aggregate numbers only — how many started and finished. Nobody is identified.',
  },
];

interface StepAccessProps {
  draft: CourseDraft;
  trackingLocked: boolean;
  onPick: (changes: Partial<CourseDraft>) => void;
}

function ModeCardBox({
  card,
  selected,
  disabled,
  blocked,
  onClick,
}: {
  card: ModeCard;
  selected: boolean;
  disabled: boolean;
  blocked: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      role='radio'
      aria-checked={selected}
      borderWidth='1px'
      borderColor={selected ? 'brand.500' : 'border'}
      bg={selected ? 'brand.soft' : 'bg.panel'}
      borderRadius='md'
      p={4}
      cursor={disabled || blocked ? 'not-allowed' : 'pointer'}
      opacity={disabled || blocked ? 0.55 : 1}
      onClick={() => {
        if (!disabled && !blocked) onClick();
      }}
    >
      <Flex align='center' gap={2.5} mb={2}>
        <Box
          w='10px'
          h='10px'
          borderRadius='full'
          bg={selected ? 'brand.500' : 'transparent'}
          borderWidth='2px'
          borderColor={selected ? 'brand.500' : 'border.emphasized'}
        />
        <Text
          fontFamily='heading'
          fontSize='sm'
          fontWeight='700'
          textTransform='uppercase'
          letterSpacing='0.04em'
          color='fg.strong'
        >
          {card.title}
        </Text>
        {blocked && card.blockedNote && (
          <Text
            ml='auto'
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.1em'
            color='fg.subtle'
          >
            {card.blockedNote}
          </Text>
        )}
      </Flex>
      <Text fontSize='sm' color='fg.muted'>
        {card.body}
      </Text>
    </Box>
  );
}

// Builder step 3: the per-course access and tracking settings. Both lock once
// the course has learner data — a recorded completion, an anonymous session or
// an assignment (tracking_locked) — so a learner's records never change shape
// underneath them. The server enforces it; this disables the UI. Sharing alone
// does not lock: until someone records progress the modes stay editable.
export default function StepAccess({ draft, trackingLocked, onPick }: StepAccessProps) {
  const sectionHead = (label: string, help: string) => (
    <>
      <Text
        fontFamily='heading'
        fontSize='2xs'
        fontWeight='700'
        textTransform='uppercase'
        letterSpacing='0.16em'
        color='fg.muted'
        mb={1}
      >
        {label}
      </Text>
      <Text fontSize='sm' color='fg.muted' mb={4}>
        {help}
      </Text>
    </>
  );

  return (
    <Flex direction='column' gap={5}>
      <Box bg='bg.panel' borderWidth='1px' borderColor='border' borderRadius='lg' shadow='sm' p={6}>
        {sectionHead(
          'Access',
          trackingLocked
            ? 'Learners have started this course — access is locked so their records keep their shape.'
            : 'Decide who can open the course link. This locks once a learner records progress or the course is assigned.',
        )}
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3.5}>
          {ACCESS_MODES.map((card) => (
            <ModeCardBox
              key={card.value}
              card={card}
              selected={draft.access_mode === card.value}
              disabled={trackingLocked}
              blocked={false}
              onClick={() => {
                const changes: Partial<CourseDraft> = {
                  access_mode: card.value as CourseDraft['access_mode'],
                };
                // An open link can't identify anyone — drop to count-only.
                if (card.value === 'open' && draft.tracking_mode === 'individual') {
                  changes.tracking_mode = 'count_only';
                }
                onPick(changes);
              }}
            />
          ))}
        </Grid>
      </Box>

      <Box bg='bg.panel' borderWidth='1px' borderColor='border' borderRadius='lg' shadow='sm' p={6}>
        {sectionHead(
          'Tracking',
          trackingLocked
            ? 'Learners have started this course — tracking is locked so their records keep their shape.'
            : 'What completion data the course records. Locks once a learner records progress or the course is assigned.',
        )}
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3.5}>
          {TRACKING_MODES.map((card) => (
            <ModeCardBox
              key={card.value}
              card={card}
              selected={draft.tracking_mode === card.value}
              disabled={trackingLocked}
              blocked={card.value === 'individual' && draft.access_mode === 'open'}
              onClick={() =>
                onPick({ tracking_mode: card.value as CourseDraft['tracking_mode'] })
              }
            />
          ))}
        </Grid>
      </Box>
    </Flex>
  );
}
